import { useMemo } from 'react'
import { Button, Empty, Table } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { List, useTable } from '@refinedev/antd'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { SettlementSummaryRoot } from '@repo/sdk'

import { bindPaginationToUrl } from '../../shared/hooks/useSyncTableWithUrl'
import { useSettlementStatusConfig } from './settlementStatusConfig'
import { createSettlementHistoryColumns } from './SettlementHistoryColumns'
import { useSettlementHistoryUrlSync } from './useSettlementHistoryUrlSync'
import {
  useSettlementHistoryFilters,
  buildSettlementHistoryFilters,
} from './useSettlementHistoryFilters'
import { SettlementHistoryFilters } from './SettlementHistoryFilters'
import { createRowClickHandler } from '@/utils/createRowClickHandler'
import { scrollXForColumns } from '@/components/responsive'

export function SettlementHistory() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const statusConfig = useSettlementStatusConfig()
  const {
    status: activeStatus,
    dateFrom,
    dateTo,
    currentPage: urlPage,
    setStatus,
    setDateRange,
    setPage,
    resetFilters,
  } = useSettlementHistoryUrlSync()

  const { tableProps, tableQuery, setFilters, currentPage, setCurrentPage } =
    useTable<SettlementSummaryRoot>({
      resource: 'settlement-history-list',
      pagination: { pageSize: 20, currentPage: urlPage },
      syncWithLocation: false,
      filters: {
        initial: buildSettlementHistoryFilters(activeStatus, dateFrom, dateTo),
      },
    })

  const { handleRefresh } = useSettlementHistoryFilters({
    status: activeStatus,
    dateFrom,
    dateTo,
    urlPage,
    currentPage,
    setFilters,
    setCurrentPage,
    refetch: () => void tableQuery.refetch(),
  })

  const columns = useMemo(
    () => createSettlementHistoryColumns({ t, statusConfig }),
    [t, statusConfig],
  )

  return (
    <List
      title={t('pages.settlements.historyTitle')}
      headerButtons={
        <Button
          data-testid="settlement-history-refresh-btn"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
        >
          {t('common.buttons.refresh')}
        </Button>
      }
    >
      <SettlementHistoryFilters
        status={activeStatus}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onStatusChange={setStatus}
        onDateRangeChange={setDateRange}
        onReset={resetFilters}
      />
      <Table
        {...tableProps}
        loading={tableQuery.isFetching}
        pagination={bindPaginationToUrl(tableProps.pagination, urlPage, setPage)}
        columns={columns}
        rowKey="settlementId"
        scroll={{ x: scrollXForColumns(columns, 1240) }}
        locale={{
          emptyText: (
            <Empty
              description={t('pages.settlements.historyEmptyState')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        onRow={(record) => ({
          'data-testid': `settlement-history-row-${record.settlementId}`,
          onClick: createRowClickHandler(
            () => void navigate(`/settlements/history/${record.settlementId}`),
          ),
          style: { cursor: 'pointer' },
        })}
      />
    </List>
  )
}
